import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useNxtImport } from '../NxtImportContext';
import { importButtonStatusIconSx, importStatusIconSx } from './ImportFieldInputStyles';
import ImportStatusTooltip from './ImportStatusTooltip';

interface ImportButtonProps {
  id?: string;
  testId?: string;
  onClickFunction: (event: React.MouseEvent<HTMLButtonElement>) => void;
  icon?: JSX.Element;
  tooltip?: string;
  color?:
    | 'default'
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
    | undefined;
}

export const ImportButton = ({
  id,
  testId,
  onClickFunction,
  tooltip,
  color,
  icon,
}: ImportButtonProps) => {
  const { imports, hasActiveImports } = useNxtImport();
  const nonInlineImports = Object.values(imports).filter(
    (importItem) => !importItem.inlineAsset,
  );

  // Check if any imports are currently uploading or queued for cancel button
  const importsProcessing =
    nonInlineImports.some(
      (importItem) =>
        importItem.isUploading || importItem.isQueued || importItem.isProcessing,
    ) && hasActiveImports;
  const isError = nonInlineImports.some((importItem) => importItem.isFailed);
  const isComplete = !hasActiveImports && nonInlineImports.length > 0 && !isError;

  return (
    <Tooltip
      disableInteractive
      title={
        <Box sx={{ display: 'grid', gap: 1, textAlign: 'center' }}>
          {tooltip}
          <ImportStatusTooltip />
        </Box>
      }
    >
      <IconButton
        id={id}
        data-testid={testId}
        onClick={onClickFunction}
        color={color}
        sx={(theme) => ({
          position: 'relative',
          '&:hover': {
            bgcolor:
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)',
          },
        })}
      >
        {icon ? icon : <UploadIcon />}
        {importsProcessing && <CircularProgress sx={importStatusIconSx} />}
        {isComplete && (
          <CheckCircleIcon
            color="success"
            sx={(theme) => importButtonStatusIconSx(theme, false)}
          />
        )}
        {isError && (
          <ErrorIcon
            color="error"
            sx={(theme) => importButtonStatusIconSx(theme, false)}
          />
        )}
      </IconButton>
    </Tooltip>
  );
};

ImportButton.displayName = 'ImportButton';
